import React, {useContext, useState, useEffect} from "react";
import {observer} from "mobx-react-lite";
import {AppStoreContext} from "../stores/AppStore";
import {
  Header,
  Form,
  Responsive,
  Card,
  CardContent,
  Checkbox
} from "semantic-ui-react";
import {useTranslation, Trans} from "react-i18next";
import {sanitize} from "../lib/sanitizeinputs";
import ModalLink from "./ModalLink";
import TermsModalDialog from "./TermsModalDialog";
import ModalWhatsNextAccepted from "./ModalWhatsNextAccepted";

import "./Register.css";
import {trackAction} from "../tracking";
import OnboardingActions from "../tracking/actions";
import {get} from "../lib/http";
import qs from "query-string";

import { FormCountry } from "./FormCountry";
import { FormPassword } from "./FormPassword";
import { FormPhone } from "./FormPhone";
import { FormTimeZones } from "./FormTimeZones";

const resetSession = () => {
  const query = qs.parse(document.location.search);
  delete query.cmId;
  get("api/clearSession")
    .then(() => window.location.search = qs.stringify(query));
}

export default observer(function Register() {
  const appStore = useContext(AppStoreContext);
  const [t] = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [emailError, setEmailError] = useState(false);

  const isWanderer = appStore.councilMemberId === 0;
  const formIsValid = isWanderer
      ? (
        appStore.prefixCode &&
        appStore.firstName &&
        appStore.lastName &&
        appStore.emailAddress &&
        (!appStore.showPhoneEntry || !!appStore.phoneNumber) &&
        appStore.countryCodeId &&
        appStore.password
      ) :
      (
        appStore.prefixCode &&
        appStore.firstName &&
        appStore.lastName &&
        appStore.emailAddress &&
        (!appStore.showPhoneEntry || !!appStore.phoneNumber) &&
        appStore.password
      )

  const formSubmitDisabled = !formIsValid || !appStore.confidentAgreement;

  const acceptAgreement1 = e => {
    if (e.target.tagName === "A") {
      return;
    }
    appStore.confidentAgreement = !appStore.confidentAgreement;
  };

  const onFormChange = e => {
    const name = e.target.name;
    const value = e.target.value;
    switch (name) {
      case "prefixCode":
        appStore.prefixCode = value ? value : appStore.defaultPrefix;
        break;
      case "firstname":
        appStore.firstName = value;
        break;
      case "lastname":
        appStore.lastName = value;
        break;
      case "email":
        appStore.emailAddress = value;
        break;
      default:
        //console.log("validateForm did not find ", name);
        break;
    }
  };

  const submitForm = e => {
    trackAction(OnboardingActions.global.Click, { action: "register" })
    setIsSaving(true);
    setEmailError(false);

    const data = {
      prefixCode: appStore.prefixCode,
      firstName: sanitize(appStore.firstName),
      lastName: sanitize(appStore.lastName),
      emailAddress: sanitize(appStore.emailAddress),
      phoneNumber: appStore.phoneNumber,
      phoneCountryCode: appStore.phoneCountryCode,
      clearTextPassword: appStore.password,
      locale: appStore.locale,
      campaign: !!appStore.campaign ? appStore.campaign : undefined,
      redirectToken: appStore.redirectToken
    };

    if (appStore.councilMemberId !== 0 && appStore.personId !== 0) {
      // The council member exists, so we will update
      data.personId = appStore.personId;
      data.councilMemberId = appStore.councilMemberId;
      appStore.updateUser(data)
      .then((success) => {
        if (success === false) {
          setIsSaving(false);
        }
      })
      .catch(err => {
        if (err.error === "CM_EMAIL_ADDRESS_ALREADY_IN_USE") {
          trackAction(OnboardingActions.HasFormError, err)
          setEmailError(true);
        }

        setIsSaving(false);
      });
    } else {
      // The council member does not exist, so we create one

      data.countryCodeId = appStore.countryCodeId;
      appStore.createUser(data)
      .then((success) => {
        if (success === false) {
          setIsSaving(false);
        }
      })
    }
  };



  // we've seen some profile overwritting which appears to be maybe
  // users sharing tokenized links/using same computer. For this purpose, we'll
  // prevent modifying of names and emails unless user is "new" or a "lead"
  const preventNameChange = !appStore.isLead && appStore.councilMemberId > 0;
  const notUserBlock = preventNameChange && (
    <div style={{display: "flex", alignItems: "center", textAlign: "left", marginBottom: "1rem"}}>
      <span onClick={resetSession} className="link">Not {appStore.firstName}? Click here.</span>
    </div>
  )

  useEffect(() => {
    appStore.loadPrefixes()
  }, [appStore])

  return (
    <div className="view view-container">
      <div className="header-container">
        <Header as="h2">{t("register")}</Header>
        <p className="header-text" dangerouslySetInnerHTML={{__html: t('register_introduction')}} />
      </div>
      {appStore.showAcceptedModal && <ModalWhatsNextAccepted />}

      <Form onSubmit={submitForm} onChange={onFormChange}>
        <Card
          centered={true}
          fluid={true}
          style={{
            maxWidth: 600,
            backgroundColor: "transparent",
            border: "none",
            boxShadow: "none"
          }}
        >
          <CardContent style={{paddingLeft: "0", paddingRight: "0"}}>
            <div style={{margin: "20px 0"}}>
              {notUserBlock}
              <Form.Group widths="equal">
                { appStore.prefixes ?
                  <Form.Dropdown
                    clearable
                    selection
                    name="prefixCode"
                    fluid
                    options={appStore.prefixes.map( p => { return { key: p.prefixCode, value: p.prefixCode, text: p.prefixName } } )}
                    placeholder={t("prefix")}
                    disabled={preventNameChange}
                    onChange={(e, r) => onFormChange({ target: r })}
                  />
                : null }
                <Form.Input
                  name="firstname"
                  type="text"
                  fluid
                  placeholder={t("first_name")+ " *"}
                  autoFocus={true}
                  disabled={preventNameChange}
                  defaultValue={appStore.firstName}
                />
                <Form.Input
                  name="lastname"
                  type="text"
                  fluid
                  placeholder={t("last_name")+ " *"}
                  disabled={preventNameChange}
                  defaultValue={appStore.lastName}
                />
              </Form.Group>
              <Form.Group widths="equal">
                <Form.Field error={emailError}>
                  <div className="ui input">
                    <input
                      className={emailError ? "input-error-no-margin error" : ""}
                      name="email"
                      type="email"
                      placeholder={t("email")+ " *"}
                      disabled={preventNameChange}
                      defaultValue={appStore.emailAddress}
                    />
                  </div>
                  {emailError && (
                    <div className="input-error input-error-position">
                      {t("email_in_use_error")}
                    </div>
                  )}
                </Form.Field>
                {appStore.showPhoneEntry && <FormPhone />}
              </Form.Group>
              {isWanderer && (
                <Form.Group widths="equal">
                  <FormCountry />
                  <FormTimeZones />
                </Form.Group>
              )}
              <FormPassword />
              <Form.Field style={{textAlign: "left"}}>
                <Checkbox
                  style={{float: "left", marginTop: 2}}
                  checked={appStore.confidentAgreement}
                  onChange={acceptAgreement1}
                />
                <div
                  style={{marginLeft: 30, cursor: "default"}}
                >
                  <Trans i18nKey="home_conf_agreement">
                    <ModalLink
                      key="confidentiality_agreement"
                      name="confidentiality_agreement"
                      text={t("confidentiality_agreement")}
                    />
                  </Trans>
                  <br/>
                  <br/>
                  <span dangerouslySetInnerHTML={{__html: t('privacy_policies_phone_1')}} />
                  <br/>
                  <br/>
                  <span dangerouslySetInnerHTML={{__html: t('privacy_policies_phone_2')}} />
                  <br/>
                  <br/>
                  <span dangerouslySetInnerHTML={{__html: t('privacy_policies_info')}} />
                </div>

              </Form.Field>
            </div>
          </CardContent>
        </Card>

        <Responsive maxWidth={400} style={{overflow: "hidden"}}>
          <div style={{margin: '20px'}}>
            <Form.Button
              primary
              fluid={true}
              disabled={formSubmitDisabled || isSaving}
              style={{marginTop: 20}}
              loading={isSaving}
            >
              {t("next")}
            </Form.Button>
          </div>
        </Responsive>
        <Responsive minWidth={401}  style={{overflow: "hidden"}}>
          <Form.Button
            primary
            fluid={false}
            disabled={formSubmitDisabled || isSaving}
            style={{marginTop: 20, marginBottom: 50}}
            floated="right"
            loading={isSaving}
          >
            {t("next")}
          </Form.Button>
        </Responsive>
      </Form>

      {/* Modal dialog for T&C */}
      <TermsModalDialog/>
    </div>
  );
});